import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { destroyEcho } from 'lib/echo';

export interface AuthUser {
  keyUser: string;
  title?: string | null;        // título (db/127): Lic., Ing., Mtra. — vacío = no se muestra
  name: string;
  firstLastName: string;
  secondLastName?: string | null;
  phone?: string | null;
  email: string;
  position?: string | null;
  idProfile: number | null;
  keyProfile?: string | null;   // key aleatoria del perfil: compárala contra constants/profileKeys (PROFILE.DASHBOARD → panel GeoAudit)
  profileName: string | null;
  isSuperadmin?: boolean;       // flag is_superadmin — bypassa permisos / acciones solo-superadmin
  permissions: string[];  // ['dashboard.view', 'users.view', ...]
}

/**
 * Quién está EMULANDO esta sesión, o null si nadie.
 *
 * Solo el nombre: es lo único que la franja de aviso necesita mostrar, y a
 * quién devolver la sesión lo decide el servidor leyendo el claim del token
 * (ver App\Services\Impersonation), no este objeto.
 */
export interface ImpersonatedBy {
  keyUser?: string;
  name: string | null;
  email?: string;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  /** Emulación activa. Lo confirma el servidor en cada /auth/check. */
  impersonatedBy: ImpersonatedBy | null;
  setAuth: (token: string, user: AuthUser) => void;
  /**
   * Entra a una emulación: cambia token y usuario DE UN GOLPE.
   *
   * Existe aparte de setAuth para que no se pueda quedar a medias: con dos
   * llamadas, un render entre ambas pintaría el menú del emulado con el token
   * del superadmin, o al revés.
   */
  setImpersonation: (token: string, user: AuthUser, by: ImpersonatedBy | null) => void;
  updateUser: (user: Partial<AuthUser>) => void;
  clearAuth: () => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      impersonatedBy: null,
      // Un login normal termina cualquier emulación: si no la limpiara, la
      // franja seguiría avisando de una sesión que ya no existe.
      setAuth: (token, user) => set({ token, user, impersonatedBy: null }),
      setImpersonation: (token, user, by) => { destroyEcho(); set({ token, user, impersonatedBy: by }); },
      updateUser: (partial) => set((s) => ({ user: s.user ? { ...s.user, ...partial } : s.user })),
      clearAuth: () => { destroyEcho(); set({ token: null, user: null, impersonatedBy: null }); },
      isAuthenticated: () => !!get().token
    }),
    { name: 'sx-auth' }
  )
);
